export const HEX_COLOR = /^#[0-9a-f]{6}$/i;

// "#1e40af" -> "226 71% 40%": the channel form the Tailwind theme reads from
// its CSS variables, as in `hsl(var(--primary))`.
export function hexToHslChannels(hex: string): string {
  const [r, g, b] = [1, 3, 5].map((start) => parseInt(hex.slice(start, start + 2), 16) / 255) as [
    number,
    number,
    number,
  ];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2;
  const delta = max - min;
  let hue = 0;
  let saturation = 0;
  if (delta > 0) {
    saturation = delta / (1 - Math.abs(2 * lightness - 1));
    if (max === r) hue = ((g - b) / delta) % 6;
    else if (max === g) hue = (b - r) / delta + 2;
    else hue = (r - g) / delta + 4;
    hue = (hue * 60 + 360) % 360;
  }
  const round = (value: number) => Math.round(value * 10) / 10;
  return `${round(hue)} ${round(saturation * 100)}% ${round(lightness * 100)}%`;
}

// The colour `weight` of the way from `base` to `over`, e.g. a pale tint of a
// brand colour on the page's background. Mixed in OKLCH, as CSS's
// `color-mix(in oklch, …)` does: lightness changes evenly to the eye and the
// hue holds, where an sRGB mix greys and darkens a tint halfway. A grey has no
// hue of its own and takes the other colour's; a result past sRGB is clipped.
export function mixHex(base: string, over: string, weight: number): string {
  const [l1, c1, h1] = hexToOklch(base);
  const [l2, c2, h2] = hexToOklch(over);
  const [hueFrom, hueTo] = [c1 < ACHROMATIC ? h2 : h1, c2 < ACHROMATIC ? h1 : h2];
  // The shorter way round the hue circle.
  let hueSpan = hueTo - hueFrom;
  if (hueSpan > 180) hueSpan -= 360;
  if (hueSpan < -180) hueSpan += 360;
  return oklchToHex(l1 + (l2 - l1) * weight, c1 + (c2 - c1) * weight, hueFrom + hueSpan * weight);
}

// Below this OKLCH chroma a colour reads as grey, and its hue is noise.
const ACHROMATIC = 0.002;

// "#rrggbb" to OKLCH: lightness 0 to 1, chroma, hue in degrees (Björn
// Ottosson's OKLab, https://bottosson.github.io/posts/oklab/).
function hexToOklch(hex: string): [number, number, number] {
  const [r, g, b] = channels(hex).map((value) => {
    const c = value / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const lightness = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const bAxis = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  return [lightness, Math.hypot(a, bAxis), (Math.atan2(bAxis, a) * 180) / Math.PI];
}

function oklchToHex(lightness: number, chroma: number, hue: number): string {
  const a = chroma * Math.cos((hue * Math.PI) / 180);
  const bAxis = chroma * Math.sin((hue * Math.PI) / 180);
  const l = (lightness + 0.3963377774 * a + 0.2158037573 * bAxis) ** 3;
  const m = (lightness - 0.1055613458 * a - 0.0638541728 * bAxis) ** 3;
  const s = (lightness - 0.0894841775 * a - 1.291485548 * bAxis) ** 3;
  const linear = [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
  return `#${linear
    .map((c) => {
      const encoded = c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;
      return Math.round(Math.min(1, Math.max(0, encoded)) * 255)
        .toString(16)
        .padStart(2, "0");
    })
    .join("")}`;
}

function channels(hex: string): [number, number, number] {
  return [1, 3, 5].map((start) => parseInt(hex.slice(start, start + 2), 16)) as [
    number,
    number,
    number,
  ];
}

// WCAG's relative luminance, 0 (black) to 1 (white).
function luminance(hex: string): number {
  const [r, g, b] = channels(hex).map((value) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// WCAG's contrast ratio, 1 to 21.
export function contrastRatio(a: string, b: string): number {
  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (light + 0.05) / (dark + 0.05);
}

// How far apart two colours are, by their furthest channel, 0 to 255:
// colours within 12 of each other read as one.
export function colorDistance(a: string, b: string) {
  if (!a.startsWith("#") || !b.startsWith("#")) return Infinity;
  const channel = (hex: string, start: number) => parseInt(hex.slice(start, start + 2), 16);
  return Math.max(...[1, 3, 5].map((start) => Math.abs(channel(a, start) - channel(b, start))));
}

// How colourful a colour is: the spread of its channels, 0 for greys.
export function chroma(hex: string): number {
  const [r, g, b] = channels(hex);
  return (Math.max(r, g, b) - Math.min(r, g, b)) / 255;
}

// `text`, or the nearest colour towards black or white that reaches
// `minimum` contrast on `background` (WCAG AA for text is 4.5).
export function withContrast(text: string, background: string, minimum = 4.5): string {
  if (contrastRatio(text, background) >= minimum) return text;
  const towards =
    contrastRatio("#000000", background) >= contrastRatio("#ffffff", background)
      ? "#000000"
      : "#ffffff";
  for (let step = 1; step <= 20; step++) {
    const candidate = mixHex(text, towards, step / 20);
    if (contrastRatio(candidate, background) >= minimum) return candidate;
  }
  return towards;
}

// A saturated colour that carries a whole field (an acid yellow, an electric
// blue), not a pale tint, a grey or a near-black.
export function isBoldColor(hex: string): boolean {
  if (!HEX_COLOR.test(hex)) return false;
  const [, saturation = 0, lightness = 0] = hexToHslChannels(hex)
    .split(" ")
    .map((part) => parseFloat(part));
  return saturation >= 45 && lightness >= 15 && lightness <= 85;
}
